import { all, call, fork, takeEvery } from '@redux-saga/core/effects';
import { createReducer, createSelector } from '@reduxjs/toolkit';
import { HEARINGTEMPLATE_INCLUDE } from '../../constants/api';
import {
  getAllHearingsSuccess,
  getAllHearingTemplatesError,
  getAllHearingTemplatesInitialize,
  getAllHearingTemplatesSuccess,
  getAllHearingTypesSuccess,
  getHearingSuccess,
  getPaginatedHearingsSuccess,
} from '../actions/api';
import { HearingTemplateConnector } from '../connectors/hearingTemplateConnector';
import { getEntityId, getRelId } from '../helpers/apiHelpers';
import { updateDtoList } from '../helpers/reducerHelpers';
import { generateGetAllApiCall } from '../helpers/sagaHelpers';
import {
  getIncludedData,
  getRequestData,
  selectAllFromApiState,
} from '../helpers/selectorHelpers';
import { hearingTypeByIdSelector } from './hearingTypeSlice';

// -------- Overview --------

// The following endpoints exist
//  - GET /hearingTemplate
//
// HearingTemplate is included in the following endpoints
//  - GET /hearing
//  - GET /hearing/{hearingId}
//  - GET /hearingType

// -------- Reducer --------
const hearingTemplateReducer = createReducer(
  {
    all: [],
    isFetchingHearingTemplates: false,
  },
  (builder) => {
    builder
      .addCase(getAllHearingTypesSuccess, (state, action) => {
        const data = getIncludedData(action);
        const hearingTemplates = data.filter(
          (include) => include.type === HEARINGTEMPLATE_INCLUDE,
        );
        return updateDtoList(state, 'all', hearingTemplates);
      })
      .addCase(getAllHearingsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const hearingTemplates = data.filter(
          (include) => include.type === HEARINGTEMPLATE_INCLUDE,
        );
        return updateDtoList(state, 'all', hearingTemplates);
      })
      .addCase(getHearingSuccess, (state, action) => {
        const data = getIncludedData(action);
        const hearingTemplates = data.filter(
          (include) => include.type === HEARINGTEMPLATE_INCLUDE,
        );
        return updateDtoList(state, 'all', hearingTemplates);
      })
      .addCase(getAllHearingTemplatesInitialize, (state) => {
        state.isFetchingHearingTemplates = true;
      })
      .addCase(getAllHearingTemplatesError, (state) => {
        state.isFetchingHearingTemplates = false;
      })
      .addCase(getAllHearingTemplatesSuccess, (state, action) => {
        const newState = Object.assign({}, state, {
          isFetchingHearingTemplates: false,
        });
        const hearingTemplates = getRequestData(action);
        return updateDtoList(newState, 'all', hearingTemplates);
      })
      .addCase(getPaginatedHearingsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const hearingTemplates = data.filter(
          (include) => include.type === HEARINGTEMPLATE_INCLUDE,
        );
        return updateDtoList(state, 'all', hearingTemplates);
      });
  },
);

// -------- Sagas ----------
export function* hearingTemplateSaga() {
  yield all([fork(getAllHearingTemplates)]);
}

function* getAllHearingTemplates() {
  yield takeEvery(
    getAllHearingTemplatesInitialize.type,
    generateGetAllApiCall(HearingTemplateConnector, function* (connector) {
      return yield call([connector, connector.getHearingTemplates]);
    }),
  );
}

// -------- Selectors --------
const selectGetAllHearingTemplates = (state) =>
  selectAllFromApiState(state, 'hearingTemplate');
const allHearingTemplatesSelector = createSelector(
  selectGetAllHearingTemplates,
  (hearingTemplates) => hearingTemplates,
);
const hearingTemplateByHearingTypeIdSelector = (hearingTypeId) =>
  createSelector(
    [allHearingTemplatesSelector, hearingTypeByIdSelector(hearingTypeId)],
    (hearingTemplates, hearingType) =>
      hearingTemplates.find(
        (h) => getEntityId(h) === getRelId(hearingType, 'hearingTemplate'),
      ),
  );

const hearingTemplateState = (state) => state.api.hearingTemplate;

const isFetchingHearingTemplatesSelector = createSelector(
  hearingTemplateState,
  (state) => state.isFetchingHearingTemplates,
);

// -------- Exporting --------
export {
  allHearingTemplatesSelector,
  hearingTemplateByHearingTypeIdSelector,
  isFetchingHearingTemplatesSelector,
};
export default hearingTemplateReducer;
